import { useCallback, useEffect, useState } from 'react';
import { listCategories, createCategory, type CategoryDto, type CategoryType } from '@/api/categories';
import { showToast } from '@/components/base/Toast';

/** Backs a "danh mục" dropdown (crop type / material group / expense category) from
 * the server instead of a hardcoded constant array — see GET/POST /categories.
 * `fallbackNames`: shown only if the server has zero entries for `type` (e.g. an
 * environment where an admin hasn't seeded this category yet) — so a picker never
 * renders empty and blocks the form it belongs to. Real server data always wins. */
export function useCategories(type: CategoryType, fallbackNames: string[] = []) {
  const [items, setItems] = useState<CategoryDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [failed, setFailed] = useState(false);

  const reload = useCallback(() => {
    setLoading(true);
    setFailed(false);
    listCategories(type)
      .then((rows) => setItems(rows))
      // A failed fetch used to be flattened into an empty list, so a broken
      // endpoint and a genuinely empty category looked identical: the dropdown
      // just had no options and said nothing about why.
      .catch(() => {
        setItems([]);
        setFailed(true);
      })
      .finally(() => setLoading(false));
  }, [type]);

  useEffect(reload, [reload]);

  const addCategory = useCallback(
    async (name: string): Promise<CategoryDto | null> => {
      const trimmed = name.trim();
      if (!trimmed) return null;
      setCreating(true);
      try {
        const created = await createCategory(type, trimmed);
        setItems((prev) => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
        return created;
      } catch (e) {
        showToast(e instanceof Error ? e.message : 'Không thêm được danh mục mới', 'error');
        return null;
      } finally {
        setCreating(false);
      }
    },
    [type],
  );

  const names = items.length > 0 ? items.map((i) => i.name) : loading ? [] : fallbackNames;

  return { items, names, loading, failed, creating, addCategory, reload };
}
